/**
 * CancelRun — Cancel until terminal, confirmed in a small dialog (sheet 13
 * callout 2; mission 6 WP07 T041; FR-011; D.8, GEN.5, B.3; research §1.17;
 * post-plan C10; post-tasks P9).
 *
 * - Absent, not disabled, once the run is terminal (C-013).
 * - A `danger-secondary` trigger opens `DialogContent size="sm"`; the footer
 *   puts the ghost keep button first, then `danger-secondary` Cancel run, busy
 *   while the POST is in flight, so a second click sends nothing.
 * - 200: `applyCancel` cancels any in-flight poll before it writes the body, so
 *   a late `analyzing` cannot resurrect the run (C10). `onRunChanged` is not
 *   called: the run is already terminal, and a refetch would be a run read
 *   after terminal (NFR-005).
 * - 409 `run-not-cancellable`: close, then `onRunChanged()`; the dispatcher
 *   renders the state the run actually reached.
 * - Anything else: the problem in the dialog, which stays open.
 * - Credits: none touched here. RunPage's observed live → terminal transition
 *   is the one credit refresh (P9).
 */
import { useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'

import { Band } from '@/components/trellis/Band'
import { ProblemBand } from '@/components/trellis/ProblemBand'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { ApiError, type Problem } from '@/lib/api'
import { cancelRun, isTerminal, type Run } from '@/lib/api/runs'
import { PROBLEM } from '@/lib/problems'
import { copyText } from '@/lib/provisional-copy'
import { applyCancel } from '@/lib/queries'

/** The drawn label (sheet 13 callout 2 "Cancel"), as the WP07 prompt names it. */
const CANCEL_RUN = 'Cancel run'

type Failure = { kind: 'problem'; problem: Problem } | { kind: 'generic' }

export interface CancelRunProps {
  run: Run
  onRunChanged(): void
}

export function CancelRun({ run, onRunChanged }: CancelRunProps) {
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<Failure | null>(null)
  const inFlight = useRef(false)

  if (isTerminal(run.state)) return null

  function onOpenChange(next: boolean) {
    if (next) setFailure(null)
    setOpen(next)
  }

  async function confirm() {
    if (inFlight.current) return
    inFlight.current = true
    setBusy(true)
    setFailure(null)
    try {
      const body = await cancelRun(run.run_id)
      await applyCancel(qc, run.run_id, body)
      setOpen(false)
    } catch (err) {
      if (err instanceof ApiError && err.problem.type === PROBLEM.RUN_NOT_CANCELLABLE) {
        setOpen(false)
        onRunChanged()
      } else if (err instanceof ApiError) {
        setFailure({ kind: 'problem', problem: err.problem })
      } else {
        setFailure({ kind: 'generic' })
      }
    } finally {
      inFlight.current = false
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="danger-secondary">{CANCEL_RUN}</Button>
      </DialogTrigger>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{copyText('cancelDialog.title')}</DialogTitle>
        </DialogHeader>
        <DialogBody className="flex flex-col gap-4">
          <DialogDescription>{copyText('cancelDialog.body')}</DialogDescription>
          {failure?.kind === 'problem' ? <ProblemBand problem={failure.problem} /> : null}
          {failure?.kind === 'generic' ? (
            <Band kind="blocked">
              <p>{copyText('genericError')}</p>
            </Band>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">{copyText('cancelDialog.keep')}</Button>
          </DialogClose>
          <Button variant="danger-secondary" loading={busy} onClick={() => void confirm()}>
            {CANCEL_RUN}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
